import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Quote, Clock, Twitter, Instagram } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";

function readingTime(html) {
  const text = (html || "").replace(/<[^>]+>/g, "").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function OpinionArticlePage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/opinion/article/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Header />
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-20 text-center">
          <h1 className="font-headline text-4xl mb-3">404 — Columna no encontrada</h1>
          <Link to="/opinion" className="text-[#ec4899] hover:underline">← Volver a Opinión</Link>
        </main>
        <Footer />
      </>
    );
  }
  if (!data) return null;

  const author = data.author || {};

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10" data-testid="opinion-article-page">
        <Link to="/opinion" className="inline-flex items-center gap-1 text-sm font-bold mb-6" style={{ color: "#ec4899" }}>
          <ArrowLeft size={14} /> OPINIÓN
        </Link>

        <div className="flex items-center gap-2 mb-4">
          <Quote size={20} style={{ color: "#ec4899" }} />
          <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#ec4899" }}>Columna de opinión</span>
        </div>

        <h1 className="font-headline uppercase text-4xl md:text-5xl leading-tight mb-4">{data.title}</h1>
        {data.lead && (
          <p className="text-lg md:text-xl mb-6 leading-relaxed" style={{ color: "var(--text-muted)" }}>{data.lead}</p>
        )}

        {/* Author block */}
        <div className="flex items-center gap-4 py-4 border-y mb-8" style={{ borderColor: "var(--border)" }}>
          {author.photo_url && (
            <img
              src={author.photo_url}
              alt={author.name}
              className="w-14 h-14 rounded-full object-cover"
              style={{ border: "3px solid #ec4899", boxShadow: "0 0 0 4px rgba(236,72,153,0.12)" }}
            />
          )}
          <div className="flex-1">
            <p className="font-bold">{author.name}</p>
            {author.role && <p className="text-xs italic" style={{ color: "#ec4899" }}>{author.role}</p>}
          </div>
          <div className="flex items-center gap-2">
            {author.twitter && (
              <a href={`https://twitter.com/${author.twitter.replace("@","")}`} target="_blank" rel="noreferrer" className="hover:opacity-70"><Twitter size={16} /></a>
            )}
            {author.instagram && (
              <a href={`https://instagram.com/${author.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="hover:opacity-70"><Instagram size={16} /></a>
            )}
            <span className="text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
              <Clock size={11} /> {readingTime(data.body)} min
            </span>
          </div>
        </div>

        {data.image && (
          <div className="aspect-[16/9] mb-8 rounded-lg overflow-hidden">
            <img src={data.image} alt={data.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="article-body prose prose-lg max-w-none"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: data.body }}
        />

        {author.bio && (
          <div className="mt-12 p-6 rounded-xl" style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}>
            <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#ec4899" }}>Sobre el autor</p>
            <div className="flex items-start gap-4">
              {author.photo_url && (
                <img src={author.photo_url} alt={author.name} className="w-16 h-16 rounded-full object-cover flex-shrink-0" style={{ border: "2px solid #ec4899" }} />
              )}
              <div>
                <p className="font-bold mb-1">{author.name}</p>
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-muted)" }}>{author.bio}</p>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
